import { useState, useEffect, createContext, useContext } from "react";
import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { 
  LayoutDashboard, 
  Package, 
  Tag, 
  ShoppingBag, 
  LogOut,
  Scale,
  Menu,
  X,
  Settings,
  Music,
  Upload,
  FolderOpen,
  Library,
  FileText,
  Users,
  Layers,
  BarChart3,
  Eye,
  ClipboardCheck
} from "lucide-react";
import { toast } from "sonner";

// Admin Auth Context
const AdminAuthContext = createContext(null);

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) throw new Error("useAdminAuth must be used within AdminAuthProvider");
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("admin_token"));
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    // Only run auth check once on mount, or when token changes after initial check
    if (!authChecked || token) {
      checkAdminAuth();
    }
  }, [token]);

  const checkAdminAuth = async () => {
    const storedToken = localStorage.getItem("admin_token");
    if (!storedToken) {
      setAdmin(null);
      setLoading(false);
      setAuthChecked(true);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/admin/me`, {
        withCredentials: false,
        headers: { Authorization: `Bearer ${storedToken}` }
      });
      setAdmin(response.data);
      setToken(storedToken);
    } catch (error) {
      console.error("Admin auth check failed:", error);
      setAdmin(null);
      localStorage.removeItem("admin_token");
      setToken(null);
    } finally {
      setLoading(false);
      setAuthChecked(true);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/admin/login`, { email, password }, { withCredentials: false });
    setAdmin({ email, is_admin: true });
    setToken(response.data.token);
    localStorage.setItem("admin_token", response.data.token);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/admin/logout`, {}, { withCredentials: false });
    } catch (e) { /* ignore */ }
    setAdmin(null);
    setToken(null);
    localStorage.removeItem("admin_token");
  };

  return (
    <AdminAuthContext.Provider value={{ admin, loading, token, login, logout, checkAdminAuth }}>
      {children}
    </AdminAuthContext.Provider>
  );
};

// Admin Layout Component
export default function AdminLayout({ children }) {
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const navItems = [
    { path: "/admin", icon: LayoutDashboard, label: "Dashboard", exact: true },
    { path: "/admin/site-config", icon: Settings, label: "Site Config" },
    { path: "/admin/landing-page", icon: FileText, label: "Landing Page" },
    { path: "/admin/vibes", icon: Music, label: "Audio Vibes" },
    { path: "/admin/bulk-upload", icon: Upload, label: "Bulk Upload" },
    { path: "/admin/file-manager", icon: FolderOpen, label: "File Manager" },
    { path: "/admin/content", icon: Library, label: "Content Manager" },
    { path: "/admin/pack-manager", icon: Layers, label: "Pack Manager" },
    { path: "/admin/bundles", icon: Package, label: "Bundles" },
    { path: "/admin/promo-codes", icon: Tag, label: "Promo Codes" },
    { path: "/admin/orders", icon: ShoppingBag, label: "Orders" },
    { path: "/admin/customers", icon: Users, label: "Customers" },
    { path: "/admin/analytics", icon: BarChart3, label: "Analytics" },
    { path: "/admin/preview-page", icon: Eye, label: "Preview Page" },
    { path: "/admin/quizzes", icon: ClipboardCheck, label: "Quizzes" },
  ];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-card border-r border-border transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-4 border-b border-border">
            <Link to="/admin" className="flex items-center gap-2">
              <Scale className="h-6 w-6 text-gold" />
              <span className="font-serif text-lg font-bold">Admin Panel</span>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                  isActive(item.path, item.exact)
                    ? "bg-gold/10 text-gold"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
              >
                <item.icon className="h-5 w-5" />
                {item.label}
              </Link>
            ))}
          </nav>

          {/* User & Logout */}
          <div className="p-4 border-t border-border">
            <p className="text-sm text-muted-foreground mb-2">{admin?.email}</p>
            <Button variant="outline" className="w-full" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="flex-1 lg:ml-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-card border-b border-border px-4 py-3 flex items-center gap-4 lg:hidden">
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
          <span className="font-serif font-semibold">Admin Panel</span>
        </header>

        {/* Page content */}
        <main className="p-6">
          {children || <Outlet />}
        </main>
      </div>
    </div>
  );
}
